<template>
  <div>
    <c-card
      title="Detailsuche"
      solid
      uppercase
    >
      <c-spinner v-if="isLoading" />

      <c-notification
        v-else-if="filters.hasError"
        title="Fehler beim Laden der Filter"
        description="Leider können die Filter zurzeit nicht geladen werden. Bitte versuchen Sie es später erneut."
        type="error"
      />

      <c-filter
        v-else
        :filters="filters.list"
        :hits="results.hits"
        @filters-change="filtersChange"
        @filters-submit="filtersSubmit"
        @ordering-change="orderingChange"
      />

      <c-spinner
        v-show="results.isLoading"
        slot="floating"
      />
    </c-card>

    <c-card
      :title="title"
      title-tag="h1"
      large
      solid
    >
      <c-spinner v-if="isLoading" />

      <c-notification
        v-else-if="results.hasError"
        title="Fehler beim Laden der SimGame"
        :description="config.errorMessage"
        type="error"
      />

      <c-results
        v-else
        :list="results.list"
      />
    </c-card>
  </div>
</template>

<script>
import CCard from '../components/CCard.vue'
import CFilter from '../components/CFilter.vue'
import CNotification from '../../../../vue/components/CNotification.vue'
import CResults from '../components/CResults.vue'
import CSpinner from '../components/CSpinner.vue'

import api from '../api'
import findTopicValue from '../functions/find-topic-value'
import getFilterItemByValue from '../functions/get-filter-item-by-value'
import getSearchConfig from '../functions/get-search-config'
import cleanUpFilterResponse from '../functions/clean-up-filter-response'
export default {
  name: 'VSearch',

  components: {
    CCard,
    CFilter,
    CNotification,
    CResults,
    CSpinner
  },

  props: {
    topicSlug: {
      type: String,
      default: undefined
    }
  },

  data () {
    return {
      isLoading: true,
      filters: {
        hasError: false,
        list: {},
        temp: JSON.parse(JSON.stringify(this.$route.query)),
        user: JSON.parse(JSON.stringify(this.$route.query))
      },
      config: getSearchConfig(),
      ordering: '',
      results: {
        hasError: false,
        hits: 0,
        list: [],
        isLoading: false
      }
    }
  },

  computed: {
    title () {
      // if topic is not set or does not exist as a filter type
      if (this.filters.user[this.config.slugToFilter.id]) {
        return `Ihre Ergebnisse – ${getFilterItemByValue(this.filters.list[this.config.slugToFilter.listId], [this.filters.user[this.config.slugToFilter.id]]).title}`
      }

      return 'Alle Ergebnisse'
    }
  },

  mounted () {
    api.getFilters()
      .then(response => {
        const filterData = cleanUpFilterResponse(response.data, this.config.filterTypes)
        this.$set(this.filters, 'list', filterData)

        if (this.topicSlug) {
          const topic = findTopicValue(this.filters.list[this.config.slugToFilter.listId], this.topicSlug)
          this.$set(this.filters.user, [this.config.slugToFilter.id], topic)
          this.$set(this.filters.temp, [this.config.slugToFilter.id], topic)
        }

        this.changeMeta()
        return api.getResults(this.filters.user)
      }, () => {
        this.filters.hasError = true
      })
      .then(response => {
        this.$set(this.results, 'list', this.orderResults(response.data.results))
        this.results.hits = response.data.count
        this.isLoading = false
      }, () => {
        this.results.error = true
      })
  },

  methods: {
    orderResults (results) {
      if (this.config.source === 'events') {
        // the backend just won't sort the events properly, so lets sort them again here.
        return results.sort((first, second) => {
          if (first.event_days[0]) {
            if (second.event_days[0]) {
              const firstDate = new Date(first.event_days[0].date)
              const secondDate = new Date(second.event_days[0].date)
              return firstDate - secondDate
            } else {
              return 0
            }
          } else {
            return 0
          }
        })
      } else {
        return results
      }
    },
    changeRoute () {
      this.changeMeta()

      // This obscure construct removes the topic id from the query
      // because we use the topic slug
      const query = JSON.parse(JSON.stringify(this.filters.user))
      delete query[this.config.slugToFilter.id]

      this.$router.replace({
        name: 'search',
        params: {
          topic: this.filters.user[this.config.slugToFilter.id]
            ? getFilterItemByValue(this.filters.list[this.config.slugToFilter.listId], [this.filters.user[this.config.slugToFilter.id]]).slug
            : null
        },
        query
      })
    },
    changeMeta () {
      let title = 'Alle Ergebnisse'
      let description = this.config.description

      if (this.filters.user[this.config.slugToFilter.id]) {
        const {
          browser_title: browserTitle,
          meta_description: metaDescription
        } = getFilterItemByValue(this.filters.list[this.config.slugToFilter.listId], [this.filters.user[this.config.slugToFilter.id]])

        if (browserTitle) {
          title = browserTitle
        }

        if (metaDescription) {
          description = metaDescription
        }
      }

      document.title = `${title} – Teach Economy`

      let metaDescriptionElement = document.querySelector('meta[name="description"]')

      if (metaDescriptionElement) {
        metaDescriptionElement.setAttribute('content', description)
      } else {
        metaDescriptionElement = document.createElement('meta')
        metaDescriptionElement.setAttribute('name', 'description')
        metaDescriptionElement.setAttribute('content', description)
        document.querySelector('head').appendChild(metaDescriptionElement)
      }
    },
    filtersChange (payload) {
      // Ordering isn't relevant for this temporary results getter
      // All we're interested in is the hit count for the results

      this.results.hasError = false
      this.filters.temp = payload

      api.getResults(this.filters.temp)
        .then(response => {
          this.results.hits = response.data.count
        }, () => {
          this.results.hasError = true
        })
    },
    filtersSubmit (payload) {
      this.results.isLoading = true
      this.$set(this.filters, 'user', payload)

      // Always get a fresh ordering from external ordering variable
      if (this.ordering) {
        this.$set(this.filters.user, 'ordering', this.ordering)
      }

      api.getResults(this.filters.user)
        .then(response => {
          this.results.isLoading = false
          this.results.hasError = false
          this.results.hits = response.data.count
          this.$set(this.results, 'list', this.orderResults(response.data.results))
        }, () => {
          this.results.isLoading = false
          this.results.hasError = true
        })

      this.changeRoute()
    },
    orderingChange (payload) {
      this.results.isLoading = true
      this.ordering = payload
      this.$set(this.filters.user, 'ordering', payload)

      api.getResults(this.filters.user)
        .then(response => {
          this.results.isLoading = false
          this.results.hasError = false
          this.$set(this.results, 'list', this.orderResults(response.data.results))
        }, () => {
          this.results.isLoading = true
          this.results.hasError = true
        })

      this.changeRoute()
    }
  }
}
</script>
